import { useTranslation } from 'react-i18next';
import type { GetVehicleResponse } from '@/api/generated/schemas';
import { ExpirySeverity } from '@/api/generated/schemas';
import { Alert } from '@/shared/ui';
import { formatExpiry } from '../../internal/vehicleFormatting';

interface VehicleExpiryBannerProps {
  vehicle: GetVehicleResponse;
}

/**
 * Banner expirací v detailu vozu. Naléhavost počítá backend podle
 * `InventoryOptions.ExpiryWarningDays`; při `None` se banner nezobrazuje. Žádný push, e-mail ani
 * persisted notification.
 */
export function VehicleExpiryBanner({ vehicle }: VehicleExpiryBannerProps) {
  const { t } = useTranslation('inventory');

  const entries = [
    {
      label: t('vehicle.banner.insurance'),
      date: vehicle.insuranceExpiry,
      severity: vehicle.insuranceSeverity,
    },
    {
      label: t('vehicle.banner.inspection'),
      date: vehicle.inspectionExpiry,
      severity: vehicle.inspectionSeverity,
    },
    {
      label: t('vehicle.banner.tachograph'),
      date: vehicle.tachographInspectionExpiry,
      severity: vehicle.tachographInspectionSeverity,
    },
  ].filter((entry) => entry.severity !== ExpirySeverity.None);

  if (entries.length === 0) {
    return null;
  }

  const hasExpired = entries.some((entry) => entry.severity === ExpirySeverity.Expired);

  return (
    <Alert
      severity={hasExpired ? 'error' : 'warning'}
      title={hasExpired ? t('vehicle.banner.expiredTitle') : t('vehicle.banner.warningTitle')}
    >
      <ul>
        {entries.map((entry) => (
          <li key={entry.label}>
            {entry.label}: {formatExpiry(t, entry.date)}
          </li>
        ))}
      </ul>
    </Alert>
  );
}
